import React from 'react';

interface NoDataStateProps {
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  illustration?: 'empty' | 'search' | 'assessment' | 'error' | 'loading';
}

const illustrations = {
  empty: (
    <svg className='w-24 h-24 text-gray-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={1.5}
          d='M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4'
      />
    </svg>
  ),
  search: (
    <svg className='w-24 h-24 text-gray-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={1.5}
          d='M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z'
      />
    </svg>
  ),
  assessment: (
    <svg className='w-24 h-24 text-gray-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={1.5}
          d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4'
      />
    </svg>
  ),
  error: (
    <svg className='w-24 h-24 text-gray-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
      <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={1.5}
          d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
      />
    </svg>
  ),
  loading: (
    <svg className='w-24 h-24 text-gray-300 animate-spin' fill='none' viewBox='0 0 24 24'>
      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
      <path
          className='opacity-75'
          fill='currentColor'
          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      />
    </svg>
  ),
};

export const NoDataState: React.FC<NoDataStateProps> = ({
  title,
  message,
  actionLabel,
  onAction,
  illustration = 'empty',
}) => {
  return (
    <div className='flex flex-col items-center justify-center min-h-[400px] bg-white rounded-lg px-4 py-12'>
      <div className='text-center max-w-lg'>
        <div className='flex justify-center mb-6 transition-transform duration-300 ease-in-out transform hover:scale-110'>
          { illustrations[illustration] }
        </div>
        <h3 className='text-xl font-semibold text-gray-900 mb-2'>
          { title }
        </h3>
        <p className='text-base text-gray-500 mb-6'>
          { message }
        </p>
        { actionLabel && onAction && (
          <button
              onClick={onAction}
              className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          >
            { actionLabel }
          </button>
        ) }
      </div>
    </div>
  );
};
